import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

interface NetworkStatusIndicatorProps {
  className?: string;
  showWhenOnline?: boolean;
  position?: 'top' | 'bottom';
}

export const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({
  className = '',
  showWhenOnline = false,
  position = 'top',
}) => {
  const { isOnline, isSlowConnection, retryConnection } = useNetworkStatus();

  const shouldShow = !isOnline || (showWhenOnline && isSlowConnection);

  if (!shouldShow) {
    return null;
  }

  const positionClasses = {
    top: 'top-0',
    bottom: 'bottom-0',
  };

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        bgColor: 'bg-red-500',
        textColor: 'text-white',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728" />
          </svg>
        ),
        message: 'No internet connection',
        showRetry: true,
      };
    }

    if (isSlowConnection) {
      return {
        bgColor: 'bg-yellow-500',
        textColor: 'text-white',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        ),
        message: 'Slow connection detected',
        showRetry: false,
      };
    }

    return null;
  };

  const statusConfig = getStatusConfig();

  if (!statusConfig) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className={`fixed left-0 right-0 z-50 ${positionClasses[position]} ${className}`}
        initial={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className={`${statusConfig.bgColor} ${statusConfig.textColor} px-4 py-3 shadow-lg`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring" }}
              >
                {statusConfig.icon}
              </motion.div>
              
              <motion.span 
                className="text-sm font-medium"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {statusConfig.message}
              </motion.span>
            </div>

            {statusConfig.showRetry && (
              <motion.button
                onClick={retryConnection}
                className="text-sm bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Retry
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NetworkStatusIndicator;